import { Elements, ExpressCheckoutElement } from '@stripe/react-stripe-js';
import { Stripe, StripeElements, StripeExpressCheckoutElementConfirmEvent, loadStripe } from '@stripe/stripe-js';
import { StripeElement } from 'components';
import { useStripeProviderOptions } from 'hooks';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { selectors, useAppSelector } from 'store';

type UnauthorizedStripeProviderProps = {
  children: ReactNode;
  showEmptyState?: boolean;
  clientSecret?: string;
  setStripe?: (stripe: Stripe | null) => void;
  setElements?: (elements: StripeElements | null) => void;
  onConfirm?: (event: StripeExpressCheckoutElementConfirmEvent) => void;
};

// @ts-ignore
const UnauthorizedStripeProvider: FC<UnauthorizedStripeProviderProps> = ({
  children,
  clientSecret,
  showEmptyState = false,
  setStripe,
  setElements,
  onConfirm,
}) => {
  const direction = useAppSelector(selectors.direction.selectDirection);
  const [stripe, setStripeInstance] = useState<Stripe | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const isActiveForm = Boolean(clientSecret);

  // The direction payload is the canonical source of the publishable key (multi-tenant: each
  // direction runs its own Stripe account). The env fallback exists for localhost, where the
  // direction lookup can 404 (no row carries the localhost origin) or Redux can hold a stale
  // persisted direction without the key — either way Elements silently never loaded.
  const publicKey = direction?.stripePublicKey || process.env.REACT_APP_PUBLISHABLE_KEY;

  useEffect(() => {
    const loadStripeInstance = async () => {
      if (publicKey) {
        console.log('🔷 [STRIPE-PROVIDER] Loading Stripe.js with key:', publicKey?.substring(0, 20) + '...');
        console.log('🔷 [STRIPE-PROVIDER] Retry attempt:', retryCount);
        try {
          const stripeInstance = await loadStripe(publicKey);
          console.log('✅ [STRIPE-PROVIDER] Stripe.js loaded successfully:', !!stripeInstance);
          console.log('✅ [STRIPE-PROVIDER] Calling setStripeInstance with:', !!stripeInstance);
          setStripeInstance(stripeInstance);
          console.log('✅ [STRIPE-PROVIDER] Calling setStripe callback with:', !!stripeInstance);
          console.log('✅ [STRIPE-PROVIDER] setStripe callback exists:', !!setStripe);
          if (setStripe) {
            setStripe(stripeInstance);
            console.log('✅ [STRIPE-PROVIDER] setStripe callback called');
          } else {
            console.log('🔴 [STRIPE-PROVIDER] setStripe callback is undefined!');
          }
          setLoadError(null); // Clear any previous errors
        } catch (error) {
          console.error('🔴 [STRIPE-PROVIDER] Failed to load Stripe.js:', error);
          console.error('🔴 [STRIPE-PROVIDER] Error details:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            stack: error instanceof Error ? error.stack : undefined,
            retryCount,
          });
          setLoadError(error instanceof Error ? error.message : 'Failed to load payment system');

          // Retry up to 3 times with exponential backoff
          if (retryCount < 3) {
            const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
            console.log(`🔄 [STRIPE-PROVIDER] Retrying in ${delay}ms...`);
            setTimeout(() => {
              setRetryCount((prev) => prev + 1);
            }, delay);
          }
        }
      } else {
        console.log('⚠️ [STRIPE-PROVIDER] No publicKey available yet');
      }
    };

    loadStripeInstance();
  }, [publicKey, retryCount]);

  const { options } = useStripeProviderOptions(isActiveForm);
  const { appearance, locale } = options;

  useEffect(() => {
    const handleMount = async () => {
      setStripe?.(await stripe);
    };

    handleMount();
  }, [stripe]);

  if (!clientSecret && !showEmptyState) {
    return null;
  }

  // Show error message if Stripe failed to load after retries
  if (loadError && retryCount >= 3) {
    return (
      <div
        style={{
          padding: '20px',
          margin: '10px 0',
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          color: '#c33',
        }}>
        <strong>Payment System Error</strong>
        <p>Unable to load the payment system. This may be due to:</p>
        <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
          <li>Network connectivity issues</li>
          <li>Ad blocker or security software blocking Stripe</li>
          <li>Firewall restrictions</li>
        </ul>
        <p style={{ marginTop: '10px' }}>
          Please try: disabling ad blockers, checking your internet connection, or using a different browser.
        </p>
        <button
          onClick={() => {
            setRetryCount(0);
            setLoadError(null);
          }}
          style={{
            marginTop: '10px',
            padding: '8px 16px',
            backgroundColor: '#c33',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}>
          Retry Loading Payment System
        </button>
      </div>
    );
  }

  // Wait for stripe to load before rendering Elements
  if (!stripe) {
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          color: '#666',
        }}>
        {retryCount > 0 ? `Loading payment system... (attempt ${retryCount + 1}/4)` : 'Loading payment system...'}
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <>
        <Elements
          stripe={stripe}
          key="elements without client secret"
          options={{
            mode: 'setup',
            currency: 'usd',
            loader: 'never',
            appearance,
            locale,
          }}>
          {children}
        </Elements>
      </>
    );
  }

  if (clientSecret) {
    return (
      <Elements stripe={stripe} key={clientSecret} options={{ clientSecret, appearance, locale }}>
        <ExpressCheckoutElement
          options={{
            wallets: { applePay: 'always', googlePay: 'always' },
            layout: { maxColumns: 1, overflow: 'never' },
          }}
          onConfirm={onConfirm || (() => {})}
          onClick={({ resolve }) => {
            resolve({
              emailRequired: true,
            });
          }}
        />
        <StripeElement setElements={setElements}>{children}</StripeElement>
      </Elements>
    );
  }

  return null;
};

export default UnauthorizedStripeProvider;
